/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {DatePipe} from '@angular/common';
import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {ToastService} from '@siemens/ix-angular';
import {PassportService} from '../../../../../services/passport.service';
import {UserService} from '../../../../../services/user.service';
import {
  AssignmentListItem,
  BatteryPassAttributeLabel,
} from '../../../../../models/BatteryPassAttributeLabel';
import {Subscription} from 'rxjs';
import {AssignmentService} from '../../../../../services/assignment.service';
import {MasterChildStringEnum} from '../../../../../models/Passport';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
} from 'ag-grid-community';
import {getIxTheme} from '@siemens/ix-aggrid';
import * as agGrid from 'ag-grid-community';
import {FeatureFlaggingService} from '../../../../../services/feature-flagging.service';
import {CxColumnHeaderComponent} from './cx-column-header/cx-column-header.component';

interface GroupedAttributeAssignments {
  [key: string]: AssignmentListItem;
}

@Component({
  selector: 'app-data-provider-assignment-list',
  templateUrl: './data-provider-assignment-list.component.html',
  styleUrl: './data-provider-assignment-list.component.scss',
  standalone: false,
  providers: [DatePipe],
})
export class DataProviderAssignmentListComponent implements OnInit, OnDestroy {
  readonly passportService = inject(PassportService);
  readonly assignmentService = inject(AssignmentService);
  readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(ToastService);
  private readonly featureFlaggingService = inject(FeatureFlaggingService);
  private readonly datePipe = inject(DatePipe);

  protected readonly Object = Object;
  passportList: GroupedAttributeAssignments = {};
  rowData: AssignmentListItem[] = [];
  loading = true;
  isCxEnabled = true;

  columnDefs: ColDef[] = [];
  gridApi: GridApi | null = null;
  gridOptions: GridOptions = {
    theme: getIxTheme(agGrid),
    rowModelType: 'clientSide',
    rowSelection: {
      mode: 'singleRow',
      enableClickSelection: false,
      checkboxes: false,
    },
    suppressMovableColumns: true,
    enableCellTextSelection: true,
    suppressCellFocus: true,
    defaultColDef: {
      sortable: false,
      autoHeight: true,
      wrapText: true,
    },
  };

  userDataSubscription?: Subscription;

  async ngOnInit() {
    const featureFlag = await this.featureFlaggingService.load();
    this.isCxEnabled = featureFlag.features.cx ?? true;
    this.initializeGrid();

    this.translateService.onLangChange.subscribe(() => {
      this.columnDefs = this.createColumnDefs();
      if (this.gridApi) {
        this.gridApi.refreshHeader();
      }
    });

    this.userDataSubscription = this.userService.userDataChange.subscribe(
      async (user) => {
        if (user && user.id) {
          const attributeList =
            await this.assignmentService.loadAttributeAssignments(user.id);
          this.passportList = this.mapPassportList(attributeList);
          this.rowData = Object.values(this.passportList);
        }
        this.loading = false;
      },
    );
  }

  private initializeGrid(): void {
    this.columnDefs = this.createColumnDefs();
  }

  createColumnDefs(): ColDef[] {
    const cxColumn: ColDef = {
      headerComponent: CxColumnHeaderComponent,
      headerValueGetter: () =>
        this.translateService.instant(
          'battery_pass.attribute_selection.fields.asset_id_label',
        ),
      flex: 3,
      minWidth: 200,
      maxWidth: 400,
      cellRenderer: (params: ICellRendererParams) => {
        const item = params.data as AssignmentListItem;
        if (!item?.assets?.length) {
          return '–';
        }
        return item.assets.join(', ');
      },
    };

    const columns: ColDef[] = [
      {
        headerValueGetter: () =>
          this.translateService.instant('battery_pass.list.id_name'),
        flex: 2,
        cellRenderer: (params: ICellRendererParams) => {
          const item = params.data as AssignmentListItem;
          if (!item) {
            return '<ix-spinner></ix-spinner>';
          }
          const total = item.completed + item.open;
          const attributeText = this.translateService.instant(
            'battery_pass.list.number_of_attributes',
            {number: item.completed, of: total},
          );
          return `<div class="passport-id-cell">
            <span>${item.passportId ?? ''}</span>
            <span class="passport-id-cell__sub">${attributeText}</span>
          </div>`;
        },
      },
      {
        headerValueGetter: () =>
          this.translateService.instant('battery_pass.list.type'),
        flex: 2,
        cellRenderer: (params: ICellRendererParams) => {
          const item = params.data as AssignmentListItem;
          if (!item) {
            return '';
          }
          const isMaster =
            item.masterChildFlag === MasterChildStringEnum.MASTER;
          const label = isMaster
            ? this.translateService.instant('battery_pass.list.master')
            : this.translateService.instant('battery_pass.list.child');
          if (isMaster) {
            return `<div style="display:flex;"><ix-pill variant="info">${label}</ix-pill></div>`;
          }
          return `<div style="display:flex;"><ix-pill variant="neutral">${label}</ix-pill></div>`;
        },
      },
      {
        headerValueGetter: () =>
          this.translateService.instant(
            'battery_pass.list.data_provision_request_date',
          ),
        flex: 2,
        sortable: true,
        sort: 'desc',
        comparator: (valueA: string, valueB: string) => {
          const dateA = valueA ? new Date(valueA).getTime() : 0;
          const dateB = valueB ? new Date(valueB).getTime() : 0;
          return dateA - dateB;
        },
        valueGetter: (params) => {
          const item = params.data as AssignmentListItem;
          return item?.createdAt ?? '';
        },
        cellRenderer: (params: ICellRendererParams) => {
          const item = params.data as AssignmentListItem;
          if (!item?.createdAt) {
            return '–';
          }
          return this.datePipe.transform(item.createdAt, 'medium') ?? '';
        },
      },
      {
        headerValueGetter: () =>
          this.translateService.instant('battery_pass.list.status'),
        minWidth: 200,
        maxWidth: 200,
        cellRenderer: (params: ICellRendererParams) => {
          const item = params.data as AssignmentListItem;
          if (!item || item.status !== 'complete') {
            return '';
          }
          const label = this.translateService.instant(
            'battery_pass.attribute_selection.submitted',
          );
          return `<div style="display:flex;"><ix-pill variant="info">${label}</ix-pill></div>`;
        },
      },
    ];

    if (this.isCxEnabled) {
      columns.splice(2, 0, cxColumn);
    }

    return columns;
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  onRowClicked(passportId: string | undefined): void {
    if (!passportId) {
      return;
    }
    const isComplete = this.passportList[passportId]?.status === 'complete';
    if (isComplete) {
      this.translateService
        .get('battery_pass.attribute_selection.already_submitted')
        .subscribe((msg: string) => {
          this.toastService.show({message: msg, type: 'info'});
        });
      return;
    }
    this.router.navigate(['/app/assign-attributes/', passportId]);
  }

  changesSubmitted(passId: string) {
    return this.passportList[passId].status === 'complete';
  }

  mapPassportList(attributeList: BatteryPassAttributeLabel[]) {
    return attributeList.reduce(
      (grouped: GroupedAttributeAssignments, item) => {
        const key = item.passportId;
        if (!key) {
          return grouped;
        }
        if (!grouped[key]) {
          grouped[key] = this.generateItem(key, item);
        } else {
          this.mergeIntoExistingItem(grouped[key], item);
        }
        return grouped;
      },
      {},
    );
  }

  private mergeIntoExistingItem(
    existing: AssignmentListItem,
    item: BatteryPassAttributeLabel,
  ): void {
    existing.completed += item.status === 'complete' ? 1 : 0;
    existing.open += item.status === 'open' ? 1 : 0;
    if (item.assetId && !existing.assets.includes(item.assetId)) {
      existing.assets.push(item.assetId);
    }
    existing.status = existing.open > 0 ? 'open' : 'complete';
    if (item.createdAt) {
      existing.createdAt =
        !existing.createdAt ||
        new Date(item.createdAt).getTime() >
          new Date(existing.createdAt).getTime()
          ? item.createdAt
          : existing.createdAt;
    }
  }

  generateItem(key: string, item: BatteryPassAttributeLabel) {
    return {
      passportId: key,
      completed: item.status === 'complete' ? 1 : 0,
      open: item.status === 'open' ? 1 : 0,
      status: item.status === 'complete' ? 'complete' : 'open',
      assets: item.assetId ? [item.assetId] : [],
      masterChildFlag: item.masterChildFlag,
      createdAt: item.createdAt,
    };
  }

  async ngOnDestroy() {
    this.userDataSubscription?.unsubscribe();
  }

  isMasterPassport(passportId: string): boolean {
    const passport = this.passportList[passportId];
    return passport.masterChildFlag === MasterChildStringEnum.MASTER;
  }
}
